import type { DashboardRow } from '@/shared/types';
import { formatCurrency, formatNumber } from '@/shared/lib/utils';
import {
  pnlRowByCuenta,
  pnlSectionById,
  pnlSections,
  type PnLFormat,
  type PnLRowConfig,
  type PnLSection,
} from './pnlConfig';

export interface PeriodColumn {
  key: string;
  /** Fecha representada por la columna (undefined para presupuesto). */
  date?: Date;
  /** Año-mes formateado, ej. "2025-04". */
  yearMonth: string;
  /** Si es columna de presupuesto. */
  isBudget: boolean;
  /** Indice de orden cronológico. */
  order: number;
}

export interface PnLTreeNode {
  type: 'section' | 'row';
  id: string;
  label: string;
  /** Sección a la que pertenece (solo para filas). */
  section?: string;
  /** Nivel visual. */
  level: number;
  format: PnLFormat;
  isTotal: boolean;
  /** Para la sección de gastos: indica si la fila es detalle o clasificación. */
  groupMode?: 'detalle' | 'clasificacion' | 'all';
  /** Datos subyacentes (solo para filas). */
  row?: DashboardRow;
  children?: PnLTreeNode[];
}

/**
 * Extrae y ordena cronológicamente las columnas de periodo que vienen
 * dinámicamente del SP_EF_DATA_FINAL.
 */
export function extractPeriodColumns(rows: DashboardRow[]): PeriodColumn[] {
  if (!rows.length) return [];

  const keys = Object.keys(rows[0]).filter(
    k => k !== 'ORDEN' && k !== 'TITULO' && k !== 'Cuenta'
  );

  const cols = keys.map((key, idx) => {
    const isBudget = key.toUpperCase().includes('PRESUPUESTO');
    const ym = key.replace(/ PRESUPUESTO/i, '').trim();
    const [year, month] = ym.split('-').map(Number);
    const date =
      year && month ? new Date(year, month - 1, 1) : undefined;

    return {
      key,
      date,
      yearMonth: ym,
      isBudget,
      // Los presupuestos se ubican justo después de su periodo real.
      order: date ? date.getTime() : Number.MAX_SAFE_INTEGER - idx,
    };
  });

  return cols.sort((a, b) => {
    if (a.date && b.date) {
      if (a.date.getTime() !== b.date.getTime()) {
        return a.date.getTime() - b.date.getTime();
      }
      return a.isBudget ? 1 : -1;
    }
    return a.order - b.order;
  });
}

/**
 * Encuentra una fila por el campo Cuenta.
 * Si hay duplicados (ej. VENTAS NETAS con/sin título), devuelve el primero
 * que tenga datos numéricos, priorizando el que sea total (TITULO = 1).
 */
export function findRow(rows: DashboardRow[], cuenta: string): DashboardRow | undefined {
  const matches = rows.filter(r => r.Cuenta === cuenta);
  if (!matches.length) return undefined;
  if (matches.length === 1) return matches[0];

  const withTotal = matches.find(r => r.TITULO === 1);
  if (withTotal) return withTotal;

  const withValue = matches.find(r =>
    Object.entries(r).some(
      ([k, v]) =>
        k !== 'ORDEN' && k !== 'TITULO' && k !== 'Cuenta' &&
        v !== null && v !== undefined && v !== '' && Number(v) !== 0
    )
  );
  return withValue ?? matches[0];
}

/**
 * Formatea un valor según el formato indicado.
 * `ratio` usa 4 decimales; `currency` usa CRC sin decimales; `number` usa 2.
 */
export function formatPnLValue(
  value: string | number | null | undefined,
  format: PnLFormat
): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);

  switch (format) {
    case 'ratio':
      return formatNumber(num, 4);
    case 'number':
      return formatNumber(num, 2);
    case 'currency':
    default:
      return formatCurrency(num);
  }
}

/**
 * Calcula la variación porcentual entre dos valores.
 * Si el valor base es 0 devuelve null (indeterminado).
 */
export function calcVariation(current: number, previous: number): number | null {
  if (previous === 0 || !Number.isFinite(previous)) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/**
 * Obtiene el valor numérico de una celda.
 */
export function getNumeric(
  row: DashboardRow | undefined,
  columnKey: string
): number | null {
  if (!row) return null;
  const raw = row[columnKey];
  if (raw === null || raw === undefined || raw === '') return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

/**
 * Resuelve la configuración visual para una fila. Si no existe en el catálogo,
 * se asigna una configuración por defecto pensada para gastos dinámicos.
 */
export function resolveRowConfig(row: DashboardRow): PnLRowConfig & { sectionObj: PnLSection } {
  const config = pnlRowByCuenta.get(row.Cuenta);
  const sectionId = config?.section ?? 'gastos-operacion';
  const section = pnlSectionById.get(sectionId)!;

  return {
    cuenta: row.Cuenta,
    label: row.Cuenta,
    section: sectionId,
    level: 2,
    format: 'currency',
    orderInSection: 500,
    ...config,
    sectionObj: section,
  };
}

/**
 * Determina si una fila de gastos de operación corresponde a la vista de
 * clasificación (agrupado) o a la vista de detalle (por centro de costo).
 * El total de la sección se muestra en ambas vistas.
 */
function resolveGroupMode(
  section: string,
  row: DashboardRow
): PnLTreeNode['groupMode'] | undefined {
  if (section !== 'gastos-operacion') return undefined;

  // El total de gastos de operación se muestra en ambas vistas.
  if (row.TITULO === 1 && row.Cuenta === 'TOTAL GASTOS OPERACIÓN') return 'all';

  // ORDEN 31: filas de clasificación configurables.
  if (row.ORDEN === 31) return 'clasificacion';
  // ORDEN 32: filas de detalle por centro de costo.
  if (row.ORDEN === 32) return 'detalle';

  return undefined;
}

/**
 * Construye el árbol jerárquico del P&L a partir de las filas crudas.
 *
 * Estrategia:
 * 1. Asigna configuración visual a cada fila.
 * 2. Agrupa las filas por sección.
 * 3. Ordena secciones y filas según la configuración declarativa.
 * 4. Retorna nodos de tipo 'section' con hijos 'row'.
 */
export function buildPnLTree(rows: DashboardRow[]): PnLTreeNode[] {
  if (!rows.length) return [];

  // Asignar metadatos a cada fila.
  const mapped = rows.map(row => {
    const cfg = resolveRowConfig(row);
    return {
      row,
      cfg,
      sortKey: `${cfg.sectionObj.order.toString().padStart(3, '0')}-${cfg.orderInSection.toString().padStart(4, '0')}`,
    };
  });

  // Agrupar por sección.
  const bySection = new Map<string, typeof mapped>();
  mapped.forEach(item => {
    const list = bySection.get(item.cfg.section) ?? [];
    list.push(item);
    bySection.set(item.cfg.section, list);
  });

  // Construir nodos de sección.
  const tree: PnLTreeNode[] = [];

  pnlSections.forEach(section => {
    const items = bySection.get(section.id);
    if (!items?.length) return;

    // Ordenar items dentro de la sección.
    items.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    const children: PnLTreeNode[] = items.map(({ row, cfg }) => ({
      type: 'row',
      id: `${section.id}-${cfg.cuenta}`,
      label: cfg.label ?? cfg.cuenta,
      section: cfg.section,
      level: cfg.level,
      format: cfg.format,
      isTotal: cfg.isTotal ?? false,
      groupMode: resolveGroupMode(cfg.section, row),
      row,
    }));

    tree.push({
      type: 'section',
      id: section.id,
      label: section.label,
      level: 0,
      format: 'currency',
      isTotal: false,
      children,
    });
  });

  return tree;
}

export interface ComparisonPeriods {
  realColumns: PeriodColumn[];
  budgetColumns: PeriodColumn[];
  latestReal?: PeriodColumn;
  prevMonth?: PeriodColumn;
  prevYear?: PeriodColumn;
}

/**
 * Determina el periodo más reciente, el mes anterior y el año anterior
 * a partir de las columnas extraídas.
 */
export function getComparisonPeriods(
  periodColumns: PeriodColumn[]
): ComparisonPeriods {
  const realColumns = periodColumns.filter(c => !c.isBudget && c.date);
  const budgetColumns = periodColumns.filter(c => c.isBudget);
  const latestReal = realColumns[realColumns.length - 1];

  const prevMonth = latestReal
    ? realColumns.find(
        c =>
          c.date &&
          c.date.getFullYear() === (latestReal.date?.getFullYear() ?? 0) &&
          c.date.getMonth() === (latestReal.date?.getMonth() ?? 0) - 1
      )
    : undefined;

  const prevYear = latestReal
    ? realColumns.find(
        c =>
          c.date &&
          c.date.getFullYear() ===
            (latestReal.date?.getFullYear() ?? 0) - 1 &&
          c.date.getMonth() === (latestReal.date?.getMonth() ?? 0)
      )
    : undefined;

  return { realColumns, budgetColumns, latestReal, prevMonth, prevYear };
}

/**
 * KPIs ejecutivos extraídos del P&L.
 */
export interface PnLKpis {
  ventasNetas: { value: number | null; change: number | null };
  volumen: { value: number | null; change: number | null };
  valorAgregado: { value: number | null; change: number | null };
  colonKg: { value: number | null; change: number | null };
}

export function buildKpis(
  rows: DashboardRow[],
  periodColumns: PeriodColumn[]
): PnLKpis {
  const getValueAndChange = (cuenta: string) => {
    const row = findRow(rows, cuenta);
    const numericCols = periodColumns.filter(c => !c.isBudget && c.date);
    if (!numericCols.length || !row) {
      return { value: null, change: null };
    }

    // Último periodo real disponible.
    const latest = numericCols[numericCols.length - 1];
    const latestValue = getNumeric(row, latest.key);

    // Periodo anterior (mes previo o año previo, el que exista).
    const previous = numericCols[numericCols.length - 2];
    const previousValue = previous ? getNumeric(row, previous.key) : null;

    const change =
      latestValue !== null && previousValue !== null
        ? calcVariation(latestValue, previousValue)
        : null;

    return { value: latestValue, change };
  };

  return {
    ventasNetas: getValueAndChange('VENTAS_NETAS'),
    volumen: getValueAndChange('TOTAL_VOLUMENES'),
    valorAgregado: getValueAndChange('VALOR_AGREGADO_TOTAL'),
    colonKg: getValueAndChange('COLON_KG_PROMEDIO'),
  };
}

/**
 * Punto de datos para una serie temporal del P&L.
 */
export interface SeriesPoint {
  month: string;
  value: number | null;
}

/**
 * Extrae una serie temporal (mes -> valor) para una cuenta específica.
 * Solo considera columnas reales (no presupuesto).
 */
export function extractSeries(
  rows: DashboardRow[],
  cuenta: string,
  periodColumns: PeriodColumn[]
): SeriesPoint[] {
  const row = findRow(rows, cuenta);
  if (!row) return [];

  const realColumns = periodColumns.filter(c => !c.isBudget && c.date);
  return realColumns.map(col => ({
    month: col.yearMonth,
    value: getNumeric(row, col.key),
  }));
}

/**
 * Determina si una cuenta es numérica de tipo ratio/número o moneda.
 */
export function getCuentaFormat(cuenta: string): PnLFormat {
  const config = pnlRowByCuenta.get(cuenta);
  return config?.format ?? 'currency';
}

/** Paleta de colores para líneas del gráfico. */
export const trendColors = [
  '#0ea5e9', // sky-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#06b6d4', // cyan-500
];
